import { EllipsisVerticalIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Member } from '@/shared/types/member';

import { OverrideDraft } from './types';

type Props = {
  override: OverrideDraft;
  member?: Member;
  disabled?: boolean;
  onEdit: () => void;
  onDelete: () => void;
};

const AgentOverrideRow = ({ override, member, disabled, onEdit, onDelete }: Props) => {
  const { t } = useTranslation();
  const name = member?.name || member?.email || override.user_id;

  return (
    <div className="flex items-center gap-2 px-4 py-2 [&:not(:last-child)]:border-b [&:not(:last-child)]:border-stroke">
      <Avatar size="sm">
        <AvatarImage src={member?.avatar} alt={name} />
        <AvatarFallback size="sm">{name[0] || '?'}</AvatarFallback>
      </Avatar>
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <p className="truncate text-sm leading-none font-medium text-foreground">{name}</p>
        <p className="truncate text-sm leading-5 text-foreground-muted">
          {t('environments.override_summary', {
            0: override.max_conversations,
            1: override.fair_distribution_limit,
          })}
        </p>
      </div>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="ghost-neutral"
            size="icon-sm"
            disabled={disabled}
            aria-label={t('environments.override_actions')}
          >
            <EllipsisVerticalIcon className="size-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={onEdit}>{t('common.edit')}</DropdownMenuItem>
          <DropdownMenuItem onClick={onDelete} className="text-destructive">
            {t('common.delete')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

export default AgentOverrideRow;
